import type { ReactElement, ReactNode } from "react";
import { useMemo } from "react";

import { Stack } from "@similarweb/ui-components/foundations";

import type { FilterCardLayout } from "../../types/filter";
import { partitionByCardLayout, splitIntoColumns } from "../../utils/layout";

import { StyledFilterCardsColumn, StyledFilterCardsGrid } from "./styles";

type GridItem = { filterCardLayout?: FilterCardLayout };

type FilterCardsGridSectionProps<TItem extends GridItem> = {
    items: readonly TItem[];
    renderItem: (item: TItem) => ReactNode;
};

export const FilterCardsGridSection = <TItem extends GridItem>({
    items,
    renderItem,
}: FilterCardsGridSectionProps<TItem>): ReactElement => {
    const { fullWidthItems, leftColumn, rightColumn } = useMemo(() => {
        const { fullWidthItems: fullWidth, gridItems } = partitionByCardLayout(items);
        const [left, right] = splitIntoColumns(gridItems);

        return { fullWidthItems: fullWidth, leftColumn: left, rightColumn: right };
    }, [items]);

    const hasGridItems = leftColumn.length + rightColumn.length > 0;

    return (
        <Stack gap="8px">
            {fullWidthItems.length > 0 ? <Stack gap="8px">{fullWidthItems.map(renderItem)}</Stack> : null}

            {hasGridItems ? (
                <StyledFilterCardsGrid>
                    <StyledFilterCardsColumn>{leftColumn.map(renderItem)}</StyledFilterCardsColumn>

                    <StyledFilterCardsColumn>{rightColumn.map(renderItem)}</StyledFilterCardsColumn>
                </StyledFilterCardsGrid>
            ) : null}
        </Stack>
    );
};
